import type { LucideIcon } from "lucide-react";
import {
	Bandage,
	Bitcoin,
	Briefcase,
	Building2,
	Car,
	CreditCard,
	Gem,
	HeartPulse,
	House,
	Landmark,
	Palette,
	PiggyBank,
	Plane,
	Sofa,
	TrendingUp,
	Umbrella,
	Zap,
} from "lucide-react";
import type { ObjPathProxy } from "ts-object-path";
import { ClientPortfolioDomainEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import {
	type PortfolioArea,
	PortfolioCategoryIds,
} from "web_myplann/Portfolio/Types/portfolioCategory";

export type PortfolioAddItemSectionId =
	| "insurance"
	| "savings"
	| "loans"
	| "property"
	| "other";

export type PortfolioAddItemId =
	| "lifeInsurance"
	| "propertyInsurance"
	| "householdInsurance"
	| "liabilityInsurance"
	| "vehicleInsurance"
	| "travelInsurance"
	| "businessInsurance"
	| "accidentInsurance"
	| "investment"
	| "pension"
	| "buildingSavings"
	| "bankProduct"
	| "crypto"
	| "commodity"
	| "mortgage"
	| "consumerLoan"
	| "businessLoan"
	| "leasing"
	| "auto"
	| "propertyItem"
	| "art"
	| "service";

type LabelProxy = ObjPathProxy<unknown, string>;

export type PortfolioAddItemConfig = {
	id: PortfolioAddItemId;
	labelKey: LabelProxy;
	icon: LucideIcon;
	area: PortfolioArea;
};

type PortfolioAddItemSection = {
	id: PortfolioAddItemSectionId;
	labelKey: LabelProxy;
	items: PortfolioAddItemConfig[];
};

export const PORTFOLIO_ADD_ITEM_SECTIONS: PortfolioAddItemSection[] = [
	{
		id: "insurance",
		labelKey: Resources.Portfolio.AddItem.Sections.insurance,
		items: [
			{
				id: "lifeInsurance",
				labelKey: Resources.Portfolio.AddItem.Items.lifeInsurance,
				icon: HeartPulse,
				area: ClientPortfolioDomainEnum.LifeInsurance,
			},
			{
				id: "propertyInsurance",
				labelKey: Resources.Portfolio.AddItem.Items.propertyInsurance,
				icon: House,
				area: ClientPortfolioDomainEnum.PropertyInsurance,
			},
			{
				id: "householdInsurance",
				labelKey: Resources.Portfolio.AddItem.Items.householdInsurance,
				icon: Sofa,
				area: ClientPortfolioDomainEnum.PropertyInsurance,
			},
			{
				id: "liabilityInsurance",
				labelKey: Resources.Portfolio.AddItem.Items.liabilityInsurance,
				icon: Umbrella,
				area: ClientPortfolioDomainEnum.PropertyInsurance,
			},
			{
				id: "vehicleInsurance",
				labelKey: Resources.Portfolio.AddItem.Items.vehicleInsurance,
				icon: Car,
				area: ClientPortfolioDomainEnum.PropertyInsurance,
			},
			{
				id: "travelInsurance",
				labelKey: Resources.Portfolio.AddItem.Items.travelInsurance,
				icon: Plane,
				area: ClientPortfolioDomainEnum.PropertyInsurance,
			},
			{
				id: "businessInsurance",
				labelKey: Resources.Portfolio.AddItem.Items.businessInsurance,
				icon: Briefcase,
				area: ClientPortfolioDomainEnum.PropertyInsurance,
			},
			{
				id: "accidentInsurance",
				labelKey: Resources.Portfolio.AddItem.Items.accidentInsurance,
				icon: Bandage,
				area: ClientPortfolioDomainEnum.PropertyInsurance,
			},
		],
	},
	{
		id: "savings",
		labelKey: Resources.Portfolio.AddItem.Sections.savings,
		items: [
			{
				id: "investment",
				labelKey: Resources.Portfolio.AddItem.Items.investment,
				icon: TrendingUp,
				area: ClientPortfolioDomainEnum.Investments,
			},
			{
				id: "pension",
				labelKey: Resources.Portfolio.AddItem.Items.pension,
				icon: PiggyBank,
				area: ClientPortfolioDomainEnum.PensionProducts,
			},
			{
				id: "buildingSavings",
				labelKey: Resources.Portfolio.AddItem.Items.buildingSavings,
				icon: Building2,
				area: ClientPortfolioDomainEnum.BuildingSavings,
			},
			{
				id: "bankProduct",
				labelKey: Resources.Portfolio.AddItem.Items.bankProduct,
				icon: Landmark,
				area: ClientPortfolioDomainEnum.BankingProducts,
			},
			{
				id: "crypto",
				labelKey: Resources.Portfolio.AddItem.Items.crypto,
				icon: Bitcoin,
				area: ClientPortfolioDomainEnum.Cryptocurrencies,
			},
			{
				id: "commodity",
				labelKey: Resources.Portfolio.AddItem.Items.commodity,
				icon: Gem,
				area: ClientPortfolioDomainEnum.Commodities,
			},
		],
	},
	{
		id: "loans",
		labelKey: Resources.Portfolio.AddItem.Sections.loans,
		items: [
			{
				id: "mortgage",
				labelKey: Resources.Portfolio.AddItem.Items.mortgage,
				icon: House,
				area: ClientPortfolioDomainEnum.Loans,
			},
			{
				id: "consumerLoan",
				labelKey: Resources.Portfolio.AddItem.Items.consumerLoan,
				icon: CreditCard,
				area: ClientPortfolioDomainEnum.Loans,
			},
			{
				id: "leasing",
				labelKey: Resources.Portfolio.AddItem.Items.leasing,
				icon: Car,
				area: ClientPortfolioDomainEnum.Leasing,
			},
			{
				id: "businessLoan",
				labelKey: Resources.Portfolio.AddItem.Items.businessLoan,
				icon: Building2,
				area: ClientPortfolioDomainEnum.BusinessLoans,
			},
		],
	},
	{
		id: "property",
		labelKey: Resources.Portfolio.AddItem.Sections.property,
		items: [
			{
				id: "auto",
				labelKey: Resources.Portfolio.AddItem.Items.auto,
				icon: Car,
				area: PortfolioCategoryIds.property,
			},
			{
				id: "propertyItem",
				labelKey: Resources.Portfolio.AddItem.Items.propertyItem,
				icon: House,
				area: PortfolioCategoryIds.property,
			},
			{
				id: "art",
				labelKey: Resources.Portfolio.AddItem.Items.art,
				icon: Palette,
				area: PortfolioCategoryIds.property,
			},
		],
	},
	{
		id: "other",
		labelKey: Resources.Portfolio.AddItem.Sections.other,
		items: [
			{
				id: "service",
				labelKey: Resources.Portfolio.AddItem.Items.service,
				icon: Zap,
				area: ClientPortfolioDomainEnum.Services,
			},
		],
	},
];
